import type { FeatureFlagKeyType } from '../types/feature-flags'
import { NotificationChannel, NotificationType } from '../types/notifications'

/**
 * The single registry of user-facing notification preference rows, shared by
 * the web settings page and the mobile notification preferences screen.
 *
 * Consumers import it by source path rather than through the package root:
 * web via its `@scrambl/shared/*` tsconfig/vite alias, mobile via
 * `apps/mobile/src/lib/notifications/notification-preference-catalog.ts`
 * (mobile's typecheck resolves `@scrambl/shared` to the built `dist/`, so a
 * root-package import would only see this file after a dist rebuild).
 *
 * Every value in `NotificationType` must appear either in one of the groups
 * below or in `NOTIFICATION_PREFERENCE_OPT_OUTS`. `notification-coverage.test.ts`
 * asserts that partition, so a new notification type cannot ship without either
 * a preference row or a written reason for not having one.
 *
 * Persistence is unchanged: rows still map 1:1 onto
 * `NotificationPreference` (`@@unique([userId, organizationId, channel, type])`).
 * Groups are presentation only — a master toggle writes the same per-type rows
 * its children would have written.
 */

/** Which account types may see a group or row. */
export type NotificationAudience = 'BUSINESS' | 'PERSONAL'

const BOTH: readonly NotificationAudience[] = ['BUSINESS', 'PERSONAL']
const BUSINESS_ONLY: readonly NotificationAudience[] = ['BUSINESS']
const PERSONAL_ONLY: readonly NotificationAudience[] = ['PERSONAL']

export interface NotificationPreferenceEntry {
  type: NotificationType
  label: string
  description: string
  /**
   * Hide this row unless the org has this module enabled. Inherited from the
   * group when the row does not name its own flag.
   */
  featureFlag?: FeatureFlagKeyType
  /** Channels this type can be delivered on. Defaults to every channel. */
  channels?: readonly (NotificationChannel | 'BROWSER')[]
}

export interface NotificationPreferenceGroup {
  key: string
  /** Section heading. Doubles as the `category` on flattened rows. */
  label: string
  description: string
  audiences: readonly NotificationAudience[]
  featureFlag?: FeatureFlagKeyType
  types: readonly NotificationPreferenceEntry[]
}

export const NOTIFICATION_PREFERENCE_GROUPS: readonly NotificationPreferenceGroup[] =
  [
    {
      key: 'my-work',
      label: 'My work',
      description: 'Work assigned to you and the deadlines attached to it',
      audiences: BOTH,
      featureFlag: 'TASKS',
      types: [
        {
          type: NotificationType.TASK_ASSIGNED,
          label: 'Task Assignments',
          description: 'When you are assigned to a task',
        },
        {
          type: NotificationType.TASK_STATUS_CHANGED,
          label: 'Task Status Changes',
          description: 'When a task you are on moves to a different status',
        },
        {
          type: NotificationType.TASK_PRIORITY_CHANGED,
          label: 'Task Priority Changes',
          description: 'When the priority of a task you are on changes',
        },
        {
          type: NotificationType.TASK_DUE_SOON,
          label: 'Task Due Soon',
          description: 'When your tasks are approaching their due date',
        },
        {
          type: NotificationType.TASK_OVERDUE,
          label: 'Overdue Tasks',
          description: 'When your tasks become overdue',
        },
        {
          type: NotificationType.TASK_SLA_BREACHED,
          label: 'Task SLA Breached',
          description: 'When a task passes the response time it promised',
        },
        {
          type: NotificationType.AGENT_REVIEW_REQUESTED,
          label: 'Work Ready for Review',
          description: 'When an agent finishes a task and needs your review',
          featureFlag: 'AI_FEATURES',
        },
        {
          type: NotificationType.BENNY_RUN_NEEDS_APPROVAL,
          label: 'Benny Needs Approval',
          description:
            'When Benny is working a task you handed over and needs your go-ahead to continue',
          featureFlag: 'BENNY_RUNS',
        },
        {
          type: NotificationType.BENNY_RUN_COMPLETED,
          label: 'Benny Finished a Task',
          description: 'When Benny finishes a task you handed over',
          featureFlag: 'BENNY_RUNS',
        },
        {
          type: NotificationType.BENNY_RUN_FAILED,
          label: "Benny Couldn't Finish",
          description:
            'When Benny stops before finishing a task you handed over',
          featureFlag: 'BENNY_RUNS',
        },
      ],
    },
    {
      key: 'mentions',
      label: 'Mentions & replies',
      description: 'When someone is speaking to you directly',
      audiences: BOTH,
      types: [
        {
          type: NotificationType.COMMENT_MENTION,
          label: 'Comment Mentions',
          description: 'When someone mentions you in a comment',
        },
        {
          type: NotificationType.COMMENT_REPLY,
          label: 'Comment Replies',
          description: 'When someone replies to your comment',
        },
        {
          type: NotificationType.TASK_MENTION,
          label: 'Task Mentions',
          description: 'When someone mentions you in a task',
          featureFlag: 'TASKS',
        },
        {
          type: NotificationType.TASK_COMMENT_ADDED,
          label: 'Task Comments',
          description: 'When someone comments on a task you are on',
          featureFlag: 'TASKS',
        },
        {
          type: NotificationType.NOTE_MENTION,
          label: 'Note Mentions',
          description: 'When someone mentions you in a note',
          featureFlag: 'NOTES',
        },
        {
          type: NotificationType.TICKET_MENTION,
          label: 'Ticket Mentions',
          description: 'When someone mentions you on a ticket',
          featureFlag: 'TICKETS',
        },
        {
          type: NotificationType.THREAD_MESSAGE,
          label: 'Discussion Messages',
          description: 'When a discussion you follow has new messages',
          featureFlag: 'DISCUSSIONS',
        },
      ],
    },
    {
      key: 'projects',
      label: 'Projects & sprints',
      description: 'Membership changes and activity in projects you watch',
      audiences: BOTH,
      featureFlag: 'PROJECTS',
      types: [
        {
          type: NotificationType.PROJECT_MEMBER_ADDED,
          label: 'Project Invitations',
          description: 'When you are added to a project',
        },
        {
          type: NotificationType.PROJECT_MEMBER_REMOVED,
          label: 'Removed From a Project',
          description: 'When you are removed from a project',
        },
        {
          type: NotificationType.PROJECT_STATUS_CHANGED,
          label: 'Project Status Changes',
          description: 'When a project you are on changes status',
        },
        {
          type: NotificationType.SPRINT_STARTED,
          label: 'Sprint Started',
          description: 'When a sprint starts on a project you are on',
        },
        {
          type: NotificationType.PROJECT_WATCH_TASK_ACTIVITY,
          label: 'Watched Project: Task Activity',
          description:
            'Tasks created, assigned, completed, or changed in projects you watch',
        },
        {
          type: NotificationType.PROJECT_WATCH_COMMENTS,
          label: 'Watched Project: Comments',
          description: 'New comments on tasks in projects you watch',
        },
        {
          type: NotificationType.PROJECT_WATCH_DOCUMENTS,
          label: 'Watched Project: Documents & Files',
          description: 'Files and links added to projects you watch',
        },
        {
          type: NotificationType.PROJECT_WATCH_PROJECT_CHANGES,
          label: 'Watched Project: Project Changes',
          description: 'Status, timeline, rename, and member changes',
        },
      ],
    },
    {
      key: 'tickets',
      label: 'Tickets & support',
      description: 'Inbox activity on the tickets you handle',
      audiences: BUSINESS_ONLY,
      featureFlag: 'TICKETS',
      types: [
        {
          type: NotificationType.TICKET_CREATED,
          label: 'New Tickets',
          description:
            'When a ticket arrives in an inbox you are notified on, or is assigned to you',
        },
        {
          type: NotificationType.TICKET_ASSIGNED,
          label: 'Ticket Assignments',
          description: 'When you are assigned to a ticket',
        },
        {
          type: NotificationType.TICKET_CUSTOMER_REPLY,
          label: 'Customer Replies',
          description:
            'When a customer replies to a ticket by email or through the portal',
        },
        {
          type: NotificationType.TICKET_STATUS_CHANGED,
          label: 'Ticket Status Changes',
          description: 'When a ticket you are on moves to a different stage',
        },
        {
          type: NotificationType.TICKET_UPDATED,
          label: 'Ticket Updates',
          description: 'When fields change on a ticket you are on',
        },
        {
          type: NotificationType.TICKET_COMMENT_ADDED,
          label: 'Ticket Comments',
          description: 'When someone comments on a ticket you are on',
        },
        {
          type: NotificationType.TICKET_FOLLOWED,
          label: 'Ticket Follows',
          description: 'When you start following a ticket',
        },
      ],
    },
    {
      key: 'timesheets',
      label: 'Timesheets & approvals',
      description: 'Submissions, approvals, and the reminders that chase them',
      audiences: BUSINESS_ONLY,
      featureFlag: 'TIME_TRACKING',
      types: [
        {
          type: NotificationType.TIMESHEET_SUBMITTED,
          label: 'Timesheet Submitted',
          description: 'When a timesheet is submitted for your approval',
        },
        {
          type: NotificationType.TIMESHEET_APPROVED,
          label: 'Timesheet Approved',
          description: 'When your timesheet is approved',
        },
        {
          type: NotificationType.TIMESHEET_CHANGES_REQUESTED,
          label: 'Timesheet Changes Requested',
          description: 'When an approver reopens your timesheet for changes',
        },
        {
          type: NotificationType.TIMESHEET_ENTRY_SUBMITTED,
          label: 'Time Entry Submitted',
          description:
            'When a single time entry is submitted for your approval',
        },
        {
          type: NotificationType.TIMESHEET_ENTRY_APPROVED,
          label: 'Time Entry Approved',
          description: 'When one of your time entries is approved',
        },
        {
          type: NotificationType.TIMESHEET_ENTRY_REJECTED,
          label: 'Time Entry Rejected',
          description: 'When one of your time entries is rejected',
        },
        {
          type: NotificationType.TIMESHEET_ENTRY_CHANGES_REQUESTED,
          label: 'Time Entry Changes Requested',
          description: 'When an approver asks for changes to a time entry',
        },
        {
          type: NotificationType.TIMESHEET_CLOSE_REMINDER,
          label: 'Timesheet Closing Reminder',
          description: 'A nudge before the pay period closes',
        },
        {
          type: NotificationType.TIMESHEET_MISSING_REMINDER,
          label: 'Missing Time Reminder',
          description: 'A nudge when days in the period have no time logged',
        },
        {
          type: NotificationType.TIMESHEET_APPROVAL_DEADLINE,
          label: 'Approval Deadline',
          description:
            'A reminder that timesheets are waiting on your approval',
        },
        {
          type: NotificationType.TIME_ENTRY_TAGGED,
          label: 'Tagged on Time Entries',
          description:
            'When a teammate tags you on their time entry so you can add it to your own timesheet',
        },
      ],
    },
    {
      key: 'budgets',
      label: 'Budgets & authorizations',
      description: 'Budget consumption, change requests, and client caps',
      audiences: BUSINESS_ONLY,
      featureFlag: 'FINANCIALS',
      types: [
        {
          type: NotificationType.BUDGET_CHANGE_REQUESTED,
          label: 'Budget Change Requested',
          description: 'When someone asks to move a budget you approve',
        },
        {
          type: NotificationType.BUDGET_CHANGE_APPROVED,
          label: 'Budget Change Approved',
          description: 'When a budget change you requested is approved',
        },
        {
          type: NotificationType.BUDGET_CHANGE_REJECTED,
          label: 'Budget Change Rejected',
          description: 'When a budget change you requested is rejected',
        },
        {
          type: NotificationType.BUDGET_USAGE_70,
          label: 'Budget 70% Used',
          description: 'When a budget you own crosses 70% consumed',
        },
        {
          type: NotificationType.BUDGET_USAGE_90,
          label: 'Budget 90% Used',
          description: 'When a budget you own crosses 90% consumed',
        },
        {
          type: NotificationType.BUDGET_OVERAGE,
          label: 'Budget Over',
          description: 'When a budget you own goes over',
        },
        {
          type: NotificationType.BUDGET_PROJECTED_OVERAGE,
          label: 'Budget Projected Over',
          description: 'When a budget is on pace to exceed its forecast',
        },
        {
          type: NotificationType.AUTHORIZATION_EXPIRING,
          label: 'Authorization Expiring',
          description: 'When a client authorization is close to its end date',
        },
        {
          type: NotificationType.AUTHORIZATION_LOW_REMAINING,
          label: 'Authorization Nearly Used',
          description: 'When a client authorization is nearly exhausted',
        },
        {
          type: NotificationType.AUTHORIZATION_EXCEEDED,
          label: 'Authorization Used Up',
          description:
            'When billing against a client authorization reaches or passes its authorized amount',
        },
        {
          type: NotificationType.AUTHORIZATION_CREATED,
          label: 'Authorization Added',
          description:
            'When someone adds a client authorization for a client you work with or assigns one to you',
        },
        {
          type: NotificationType.AUTHORIZATION_UPDATED,
          label: 'Authorization Changed',
          description:
            "When a client authorization's amount, dates, or status changes, including approval",
        },
      ],
    },
    {
      key: 'contracts',
      label: 'Contracts',
      description: 'Contract lifecycle alerts and approval requests',
      audiences: BUSINESS_ONLY,
      featureFlag: 'CONTRACTS',
      types: [
        {
          type: NotificationType.CONTRACT_TERM_ENDING,
          label: 'Contract Term Ending',
          description:
            'When a contract term is 60 or 30 days from ending, or its reminder date is reached',
        },
        {
          type: NotificationType.CONTRACT_MONTH_TO_MONTH_ACTIVE,
          label: 'Running Month to Month',
          description:
            'When a contract passes its end date and keeps running month to month without new paper',
        },
        {
          type: NotificationType.CONTRACT_SCHEDULE_LINE_DUE,
          label: 'Billing Line Coming Due',
          description:
            'When a planned or ready billing schedule line on a contract is due within a week',
        },
        {
          type: NotificationType.CONTRACT_APPROVAL_REQUESTED,
          label: 'Contract Approval Requested',
          description: 'When a contract needs your approval',
        },
        {
          type: NotificationType.CONTRACT_APPROVAL_APPROVED,
          label: 'Contract Approved',
          description: 'When a contract you sent for approval is approved',
        },
        {
          type: NotificationType.CONTRACT_APPROVAL_REJECTED,
          label: 'Contract Rejected',
          description: 'When a contract you sent for approval is rejected',
        },
      ],
    },
    {
      key: 'invoices',
      label: 'Invoices & payments',
      description: 'What happens to invoices after they leave the building',
      audiences: BUSINESS_ONLY,
      featureFlag: 'INVOICES',
      types: [
        {
          type: NotificationType.INVOICE_SENT,
          label: 'Invoice Sent',
          description: 'When an invoice is sent to a client',
        },
        {
          type: NotificationType.INVOICE_OPENED,
          label: 'Invoice Opened',
          description: 'When a client opens an invoice you sent',
        },
        {
          type: NotificationType.INVOICE_PAYMENT_RECORDED,
          label: 'Payment Recorded',
          description: 'When a payment is recorded on an invoice',
        },
        {
          type: NotificationType.INVOICE_OVERDUE,
          label: 'Invoice Overdue',
          description: 'When an invoice you sent passes its due date unpaid',
        },
        {
          type: NotificationType.INVOICE_PAYMENT_FAILED,
          label: 'Online Payment Failed',
          description:
            "When a client's card or bank payment on an invoice fails",
          featureFlag: 'ONLINE_PAYMENTS',
        },
        {
          type: NotificationType.INVOICE_PAYMENT_NEEDS_REVIEW,
          label: 'Online Payment Needs Review',
          description:
            'When an online payment could not be applied to an invoice, or a client disputes one',
          featureFlag: 'ONLINE_PAYMENTS',
        },
        {
          type: NotificationType.INVOICE_APPROVAL_REQUESTED,
          label: 'Invoice Approval Requested',
          description: 'When an invoice is sent to a client for approval',
        },
        {
          type: NotificationType.INVOICE_APPROVAL_APPROVED,
          label: 'Invoice Approved by Client',
          description: 'When a client approves an invoice you sent',
        },
        {
          type: NotificationType.INVOICE_APPROVAL_CHANGES_REQUESTED,
          label: 'Invoice Changes Requested',
          description: 'When a client asks for changes to an invoice',
        },
        {
          type: NotificationType.CLIENT_BILLING_REVIEW_OVERDUE,
          label: 'Billing Review Overdue',
          description:
            'When a client billing review assigned to you passes its due date unfinished',
        },
      ],
    },
    {
      key: 'clients',
      label: 'Clients & health',
      description: 'Health bands and automated lifecycle moves',
      audiences: BUSINESS_ONLY,
      types: [
        {
          type: NotificationType.ENTITY_HEALTH_AT_RISK,
          label: 'Client At Risk',
          description:
            'When a client you are on drops into the at-risk health band',
          featureFlag: 'CLIENT_HEALTH',
        },
        {
          type: NotificationType.ENTITY_HEALTH_DECLINED,
          label: 'Client Health Declining',
          description: 'When a client slips from healthy to the watch band',
          featureFlag: 'CLIENT_HEALTH',
        },
        {
          type: NotificationType.ENTITY_HEALTH_IMPROVED,
          label: 'Client Health Improved',
          description: 'When a client recovers from a lower health band',
          featureFlag: 'CLIENT_HEALTH',
        },
        {
          type: NotificationType.DEAL_HEALTH_AT_RISK,
          label: 'Deal At Risk',
          description: 'When a deal you own drops into the risk health band',
          featureFlag: 'DEALS',
        },
        {
          type: NotificationType.LIFECYCLE_STAGE_AUTOMATED,
          label: 'Automated Stage Changes',
          description:
            'When automation moves a client or contact lifecycle stage (won deal, health change)',
          featureFlag: 'ENTITIES',
        },
      ],
    },
    {
      key: 'resourcing',
      label: 'Team & resourcing',
      description: 'Demand requests, utilization, and burnout signals',
      audiences: BUSINESS_ONLY,
      featureFlag: 'TEAM',
      types: [
        {
          type: NotificationType.DEMAND_SUBMITTED,
          label: 'Demand Submitted',
          description: 'When a resourcing request is submitted for triage',
        },
        {
          type: NotificationType.DEMAND_TRIAGED,
          label: 'Demand Triaged',
          description: 'When a resourcing request you raised is triaged',
        },
        {
          type: NotificationType.DEMAND_FULFILLED,
          label: 'Demand Fulfilled',
          description: 'When a resourcing request is filled',
        },
        {
          type: NotificationType.DEMAND_RETURNED_TO_ACTIVE,
          label: 'Demand Reopened',
          description: 'When a filled resourcing request goes back to active',
        },
        {
          type: NotificationType.DEMAND_APPROACHING_START,
          label: 'Demand Starting Soon',
          description: 'When an unfilled request is close to its start date',
        },
        {
          type: NotificationType.DEMAND_DEAL_STATUS_CHANGED,
          label: 'Demand Deal Updated',
          description: 'When the deal behind a resourcing request changes',
        },
        {
          type: NotificationType.BURNOUT_RISK_TRANSITION,
          label: 'Burnout Risk Changes',
          description:
            'When someone you manage moves between burnout risk levels',
        },
        {
          type: NotificationType.RESOURCE_WEEKLY_DIGEST,
          label: 'Weekly Digest',
          description: 'A Monday summary of your allocations and utilization',
          channels: [NotificationChannel.EMAIL],
        },
      ],
    },
    {
      key: 'reminders',
      label: 'Date reminders',
      description: 'Dates you asked to be reminded about',
      audiences: BOTH,
      types: [
        {
          type: NotificationType.COLLECTION_DATE_REMINDER,
          label: 'Collection Date Reminders',
          description:
            'When a date field on a collection entry (renewal, bill, expiry) comes due',
          featureFlag: 'COLLECTION_REMINDERS',
        },
        {
          type: NotificationType.BIRTHDAY_REMINDER,
          label: 'Birthday Reminders',
          description: 'When a contact birthday is coming up',
          featureFlag: 'CONTACT_BIRTHDAYS',
        },
      ],
    },
    {
      key: 'system',
      label: 'System & imports',
      description: 'Background jobs and platform notices',
      audiences: BOTH,
      types: [
        {
          type: NotificationType.IMPORT_COMPLETED,
          label: 'Import Completed',
          description: 'When an import you started finishes',
        },
        {
          type: NotificationType.IMPORT_FAILED,
          label: 'Import Failed',
          description: 'When an import you started fails',
        },
        {
          type: NotificationType.MEETING_NOTES_READY,
          label: 'Meeting Notes Ready',
          description: 'When a recording finishes processing into notes',
          featureFlag: 'MEETING_NOTES',
        },
        {
          type: NotificationType.MEETING_NOTES_FAILED,
          label: 'Meeting Notes Failed',
          description: 'When a recording could not be processed',
          featureFlag: 'MEETING_NOTES',
        },
        {
          type: NotificationType.AI_PROVIDER_KEY_INVALID,
          label: 'AI Provider Key Problems',
          description:
            "When your organization's own model provider rejects its key (owners and admins)",
          featureFlag: 'BYO_AI',
        },
        {
          type: NotificationType.SYSTEM_MAINTENANCE,
          label: 'System Updates',
          description: 'Important system announcements and maintenance notices',
        },
      ],
    },
    {
      key: 'engagement',
      label: 'Streaks & goals',
      description: 'Your badges, weekly goals and weekly recap',
      audiences: BUSINESS_ONLY,
      featureFlag: 'GAMIFICATION',
      types: [
        {
          type: NotificationType.ACHIEVEMENT_UNLOCKED,
          label: 'Badges Earned',
          description: 'When you earn a new badge',
        },
        {
          type: NotificationType.GOAL_ACHIEVED,
          label: 'Weekly Goal Reached',
          description: 'When you hit one of your weekly goals',
        },
        {
          type: NotificationType.ENGAGEMENT_WEEKLY_RECAP,
          label: 'Your Week Recap',
          description: 'A Sunday summary of your week and streak',
        },
      ],
    },
    {
      key: 'family',
      label: 'Family & stars',
      description: 'The daily habit loop in your family space',
      audiences: PERSONAL_ONLY,
      types: [
        {
          type: NotificationType.STREAK_MILESTONE,
          label: 'Streak Milestones',
          description: 'When someone in the family hits a chore streak',
        },
        {
          type: NotificationType.STARS_WEEKLY_SUMMARY,
          label: 'Weekly Stars Recap',
          description: 'A weekly summary of stars and chores',
        },
        {
          type: NotificationType.LIST_THRESHOLD,
          label: 'List Getting Long',
          description: 'When a shared list piles up with open items',
        },
      ],
    },
  ]

/**
 * Types that deliberately have NO preference row, with the reason. Every entry
 * here is an explicit product decision, not an oversight — the coverage test
 * treats this map as the only sanctioned way to leave a type off the settings
 * page.
 */
export const NOTIFICATION_PREFERENCE_OPT_OUTS: Readonly<
  Partial<Record<NotificationType, string>>
> = {
  // --- Declared in the enum but never emitted by apps/api (verified by
  // grepping every emission site). A toggle for these would be a lie.
  [NotificationType.DEAL_ASSIGNED]:
    'Not emitted: deal assignment writes no notification.',
  [NotificationType.DEAL_STATUS_CHANGED]:
    'Not emitted: deal stage changes write no notification.',
  [NotificationType.CONTACT_ASSIGNED]:
    'Not emitted: contact assignment writes no notification.',
  [NotificationType.CONTACT_UPDATED]:
    'Not emitted: contact updates write no notification.',
  [NotificationType.ENTITY_MEMBER_ADDED]:
    'Not emitted: entity membership changes write no notification.',
  [NotificationType.ENTITY_STATUS_CHANGED]:
    'Not emitted: entity status changes write no notification.',
  [NotificationType.PROJECT_DUE_SOON]:
    'Not emitted: only present in the notification-creator copy/priority maps.',
  [NotificationType.SPRINT_COMPLETED]:
    'Not emitted: sprint completion is a Slack channel event only.',
  [NotificationType.COMMENT_REACTION]:
    'Not emitted: reactions fan out over websockets, never as notifications.',
  [NotificationType.DEMAND_MATCH_AVAILABLE]:
    'Not emitted: demand matching writes no notification yet.',
  [NotificationType.SUBSCRIPTION_RENEWED]:
    'Not emitted: billing lifecycle writes no notification.',
  [NotificationType.FEATURE_ANNOUNCEMENT]:
    'Not emitted: announcements ship through the changelog, not notifications.',
  [NotificationType.TIMESHEET_REJECTED]:
    'Not emitted: there is no period-level reject. Approvers reject single entries (Time Entry Rejected) or reopen the period (Timesheet Changes Requested).',

  // --- Emitted, but a staff-facing toggle would be wrong.
  [NotificationType.SUBSCRIPTION_EXPIRING]:
    'Emitted by the no-card trial lifecycle sweep to owners/admins only (trial ending, grace, read-only). Billing-critical: muting it would let a workspace lock without warning.',
  [NotificationType.CUSTOM]:
    'Generic catch-all shared by workflow actions and "your export is ready" receipts. One switch cannot describe it, and switching it off would silence a direct response to the user\'s own action.',
  [NotificationType.CLIENT_PORTAL_TASK_READY]:
    'Portal-recipient type. Managed by the client on the client portal settings page.',
  [NotificationType.CLIENT_PORTAL_NOTE_SHARED]:
    'Portal-recipient type. Managed by the client on the client portal settings page.',
}

/**
 * Group rows flattened into the `NotificationTypeConfig` shape the preferences
 * matrix consumes. Group-level `featureFlag` / `audiences` are pushed down onto
 * each row so the component only has to reason about one list.
 */
export function flattenNotificationPreferenceCatalog(
  groups: readonly NotificationPreferenceGroup[] = NOTIFICATION_PREFERENCE_GROUPS,
) {
  return groups.flatMap(group =>
    group.types.map(entry => ({
      type: entry.type,
      label: entry.label,
      description: entry.description,
      category: group.label,
      channels: entry.channels,
      featureFlag: entry.featureFlag ?? group.featureFlag,
      audiences: group.audiences,
    })),
  )
}

/** Every type that has a preference row, in catalog order. */
export const NOTIFICATION_PREFERENCE_TYPES: readonly NotificationType[] =
  NOTIFICATION_PREFERENCE_GROUPS.flatMap(group =>
    group.types.map(entry => entry.type),
  )
